import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import PageHeader from '../shell/PageHeader';
import { NutritionKpiRow } from './NutritionKpiRow';
import { NutritionFoliarRoundsCard } from './NutritionFoliarRoundsCard';
import { NutritionSignoffCard } from './NutritionSignoffCard';
import { NutritionAlertStack } from './NutritionAlertStack';
import { NutritionNpkBand } from './NutritionNpkBand';
import FertilizerStockUsageWidget from './FertilizerStockUsageWidget';
import { getNutritionSummary } from '../../services/nutritionService';
import type { NutritionSummary } from '../../services/nutritionService';
import { RowCard } from '../patterns';
import './nutrition.css';

const NutritionDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<NutritionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getNutritionSummary();
        if (!cancelled) setSummary(data);
      } catch (err: any) {
        Sentry.captureException(err);
        if (!cancelled) setError(err?.message ?? 'load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div role="status" aria-label={t('nutrition.dashboard.loading')} style={{ padding: 24, textAlign: 'center' }}>
        {t('nutrition.dashboard.loading')}
      </div>
    );
  }
  if (error || !summary) {
    return (
      <div role="alert" style={{ padding: 16, color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 6, margin: 16 }}>
        {t('nutrition.dashboard.error')}
      </div>
    );
  }

  // Blocks render independently off the payload sections the viewer is allowed
  // to see — inventory and audit are separate caps, so don't gate them as one.
  const showAdminLayout = !!summary.inventory || !!summary.audit;

  const actions = showAdminLayout ? (
    <>
      <button type="button" className="pat-btn pat-btn--subtle" onClick={() => navigate('/nutrition/foliar/setup')}>
        {t('nutrition.dashboard.quickActions.newProgramme')}
      </button>
      <button type="button" className="pat-btn pat-btn--outline" onClick={() => navigate('/nutrition/foliar')}>
        {t('nutrition.dashboard.quickActions.prepareSheets')}
      </button>
      {summary.inventory && (
        <button type="button" className="pat-btn pat-btn--primary" onClick={() => navigate('/nutrition/inventory')}>
          {t('nutrition.dashboard.quickActions.manageStock')}
        </button>
      )}
    </>
  ) : (
    <button type="button" className="pat-btn pat-btn--primary" onClick={() => navigate('/nutrition/foliar')}>
      {t('nutrition.dashboard.quickActions.signOff')}
    </button>
  );

  return (
    <div data-testid="nutrition-dashboard" className="ntr-dash">
      <PageHeader
        title={t('nutrition.dashboard.title')}
        meta={t('nutrition.dashboard.meta', { orchards: summary.appliedThisMonth.orchards })}
        actions={actions}
      />
      {/*
        #443 (review): both scope pills now live inside the element they
        describe — "Foliar and soil" in the Applied-this-month KPI tile
        (NutritionKpiRow), "Foliar only" in the foliar rounds card header.
        They used to sit in bare, unclassed divs beside whole groups, where
        each one mislabelled its neighbours.
      */}
      <NutritionKpiRow summary={summary} />
      {showAdminLayout ? (
        <>
          <div className="ntr-dash-body">
            <div className="ntr-dash-body__left">
              <NutritionFoliarRoundsCard programmes={summary.rounds} />
              <NutritionSignoffCard
                data={summary.signoffsOutstanding}
                soilOutstanding={summary.soil.outstanding}
              />
              <section className="ntr-dash-card" data-testid="nutrition-soil-coverage-card">
                <header className="ntr-dash-card__head">
                  <h3 className="ntr-dash-card__title">{t('nutrition.dashboard.soilCoverage')}</h3>
                </header>
                <div className="ntr-dash-card__body">
                  {summary.soil.coverage.length === 0 ? (
                    <p className="ntr-dash-card__empty">{t('nutrition.dashboard.soilCoverageEmpty')}</p>
                  ) : (
                    summary.soil.coverage.map((prog) => (
                      <React.Fragment key={prog.programId}>
                        <div className="ntr-dash-prog__season">{prog.season}</div>
                        {/*
                          An active program with no sessions yet gets an
                          explicit line: summariseSoilCoverage returns
                          `months: []` for it, which otherwise rendered a bare
                          season heading and nothing else. It must NOT render
                          "0/0" — unknown is not zero.
                        */}
                        {prog.months.length === 0 && (
                          <p
                            className="ntr-dash-card__empty"
                            data-testid={`soil-coverage-none-${prog.programId}`}
                          >
                            {t('nutrition.dashboard.soilCoverageNoSessions')}
                          </p>
                        )}
                        {prog.months.map((m) => (
                          <RowCard key={m.month} testId={`soil-coverage-${prog.programId}-${m.month}`}>
                            <div className="ntr-dash-round">
                              <span className="ntr-dash-round__name">{m.month}</span>
                              {/*
                                signedOff is a SEPARATE bucket from complete in
                                summariseSoilCoverage — and it is the terminal
                                done state. Counting only `complete` rendered a
                                fully signed-off month as 0/2. Everywhere else
                                in nutrition treats signed_off as done:
                                nutritionDashboardService.js:122 and
                                FoliarBoardPage.tsx:170.
                              */}
                              <span className="ntr-dash-round__counts">{m.complete + m.signedOff}/{m.total}</span>
                            </div>
                          </RowCard>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </section>
            </div>
            <div className="ntr-dash-body__right">
              <NutritionAlertStack
                inventory={summary.inventory}
                needsLinking={summary.audit?.needsLinking ?? 0}
              />
            </div>
          </div>
          {summary.fertilizerStockUsage && (
            <FertilizerStockUsageWidget rows={summary.fertilizerStockUsage} />
          )}
          {/* NutritionNpkBand returns null when there is no audit block or empty orchards. */}
          <NutritionNpkBand audit={summary.audit} />
        </>
      ) : (
        <NutritionSignoffCard
          data={summary.signoffsOutstanding}
          soilOutstanding={summary.soil.outstanding}
        />
      )}
    </div>
  );
};

export default NutritionDashboardPage;
